/**
 * bundle-swatches.js
 * Ecuaa — Bundle product page enhancements for Dawn theme
 *
 * Supports TWO swatch option types:
 *   - "Yarn Color" (single color) -> alt text "color-[name]" -> ONE preview thumbnail
 *   - "Color Duo" (two-color pairing) -> alt text "duoa-[duo-name]" + "duob-[duo-name]" -> TWO preview thumbnails
 *
 * BEHAVIOR:
 *   - Main gallery image -> switches based on KIT selection
 *   - Small preview thumbnail(s) below swatches -> switch based on COLOR/DUO selection
 */

(function () {
  'use strict';

  const COLOR_MAP = {
    'pure cloud white':  '#F2EDE3',
    'golden honey':      '#D4922F',
    'harvest yellow':    '#C8A52E',
    'deep purple':       '#3D1F7A',
    'pastel lavender':   '#C3AED6',
    'rose quartz':       '#D49098',
    'coral orange':      '#CC6040',
    'soft sage':         '#8DAA78',
    'forest olive':      '#5A6932',
    'obsidian black':    '#1A1A1A',
    'ecru':              '#F0E6CE',
    'earth brown':       '#7A4D2E',
    'gray':              '#9A9A96',
    'saffron gold':      '#D4821A',
    'oatmeal tan':       '#C8A57A',
    'canyon brown':      '#9A4826',
    'cacao brown':       '#6A3016',
    'espresso black':    '#1E100A',
    'cloud white':       '#F8F4EE',
  };

  // Color Duo name -> [hex1, hex2] for split-swatch rendering
  const DUO_MAP = {
    'golden fields':     ['#D4922F', '#5A6932'],
    'harvest sage':      ['#C8A52E', '#8DAA78'],
    'sunset bloom':      ['#CC6040', '#D49098'],
    'violet dusk':       ['#3D1F7A', '#C3AED6'],
    'classic contrast':  ['#F2EDE3', '#1A1A1A'],
  };

  const KIT_IMAGE_KEYS = {
    'alpaca kit': 'kit-alpaca',
    'puppy kit':  'kit-puppy',
    'bear kit':   'kit-bear',
  };

  const COLOR_IMAGE_PREFIX = 'color-';
  const DUOA_IMAGE_PREFIX = 'duoa-';
  const DUOB_IMAGE_PREFIX = 'duob-';

  function normalise(str) {
    return str.toLowerCase().trim();
  }

  function toImageKey(prefix, name) {
    return prefix + normalise(name).replace(/\s+/g, '-');
  }

  function findSwatchFieldset(root) {
    root = root || document;
    const fieldsets = root.querySelectorAll(
      '.product-form__input--pill, .product-form__input--swatch'
    );
    for (const fs of fieldsets) {
      const legend = fs.querySelector('legend');
      if (!legend) continue;
      const text = normalise(legend.textContent);
      if (text.includes('yarn color')) return { fieldset: fs, kind: 'color' };
      if (text.includes('color duo')) return { fieldset: fs, kind: 'duo' };
    }
    return null;
  }

  function findKitFieldset(root) {
    root = root || document;
    const fieldsets = root.querySelectorAll(
      '.product-form__input--pill, .product-form__input--swatch'
    );
    for (const fs of fieldsets) {
      const legend = fs.querySelector('legend');
      if (legend && normalise(legend.textContent).trim() === 'kit') {
        return fs;
      }
    }
    return null;
  }

  function buildImageMap() {
    const map = {};
    document.querySelectorAll('[data-media-id]').forEach((item) => {
      const img = item.querySelector('img');
      if (!img || !img.alt) return;
      const alt = normalise(img.alt);
      const entry = { mediaId: item.dataset.mediaId, url: img.src || img.currentSrc };

      Object.values(KIT_IMAGE_KEYS).forEach((key) => {
        if (alt.includes(key)) map[key] = entry;
      });
      if (alt.startsWith(COLOR_IMAGE_PREFIX)) map[alt] = entry;
      if (alt.startsWith(DUOA_IMAGE_PREFIX)) map[alt] = entry;
      if (alt.startsWith(DUOB_IMAGE_PREFIX)) map[alt] = entry;
    });
    return map;
  }

  function activateGalleryImage(mediaId) {
    if (!mediaId) return;
    const gallery = document.querySelector('media-gallery');
    if (gallery && typeof gallery.setActiveMedia === 'function') {
      gallery.setActiveMedia(mediaId, true);
    }
  }

  function isLightColor(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return false;
    const r = parseInt(result[1], 16);
    const g = parseInt(result[2], 16);
    const b = parseInt(result[3], 16);
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.78;
  }

  // ── Bundle label ─────────────────────────────────────────
  function addBundleLabel(variantSelects) {
    if (!variantSelects) return;
    document.querySelectorAll('.bundle-section-label').forEach((el) => {
      if (el.nextElementSibling !== variantSelects) el.remove();
    });
    if (
      variantSelects.previousElementSibling &&
      variantSelects.previousElementSibling.classList.contains('bundle-section-label')
    ) {
      return;
    }
    const label = document.createElement('p');
    label.className = 'bundle-section-label';
    label.textContent = 'Bundle';
    variantSelects.insertAdjacentElement('beforebegin', label);
  }

  // ── Swatch sync (no rebuild) ───────────────────────────────
  function syncSwatchSelection(fieldset) {
    const legend = fieldset.querySelector('legend');
    const label = legend && legend.querySelector('.bundle-swatch-selected-label');
    fieldset.querySelectorAll('input[type="radio"]').forEach((radio) => {
      const swatch = fieldset.querySelector(`.bundle-swatch[data-radio-id="${radio.id}"]`);
      if (!swatch) return;
      const isUnavailable = radio.classList.contains('disabled');
      swatch.classList.toggle('is-unavailable', isUnavailable);
      swatch.setAttribute('aria-disabled', isUnavailable ? 'true' : 'false');
      if (radio.checked) {
        swatch.classList.add('is-selected');
        swatch.setAttribute('aria-pressed', 'true');
        if (label) label.textContent = radio.value;
      } else {
        swatch.classList.remove('is-selected');
        swatch.setAttribute('aria-pressed', 'false');
      }
    });
  }

  // ── Build swatches (color or duo) ───────────────────────────
  function buildSwatches(fieldset, kind, imageMap) {
    if (!fieldset) return;

    const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    const signature = radios
      .map((r) => r.value + ':' + (r.classList.contains('disabled') ? '0' : '1') + ':' + (r.checked ? '1' : '0'))
      .join('|');

    fieldset.classList.add('bundle-color-option');

    const needsRebuild = fieldset.dataset.swatchSignature !== signature;
    fieldset.dataset.swatchSignature = signature;

    const legend = fieldset.querySelector('legend');
    let selectedLabel = legend.querySelector('.bundle-swatch-selected-label');
    if (!selectedLabel) {
      selectedLabel = document.createElement('span');
      selectedLabel.className = 'bundle-swatch-selected-label';
      legend.appendChild(selectedLabel);
    }

    if (!needsRebuild) {
      syncSwatchSelection(fieldset);
      updatePreview(fieldset, kind, imageMap);
      return;
    }

    const existingRow = fieldset.querySelector('.bundle-swatch-row');
    if (existingRow) existingRow.remove();

    const container = document.createElement('div');
    container.className = 'bundle-swatch-row';
    container.setAttribute('role', 'group');
    container.setAttribute('aria-label', kind === 'duo' ? 'Color duo' : 'Yarn colour');

    radios.forEach((radio) => {
      const name = radio.value;
      const isSelected = radio.checked;
      const isUnavailable = radio.classList.contains('disabled');

      const swatch = document.createElement('button');
      swatch.type = 'button';
      swatch.className = 'bundle-swatch';
      swatch.dataset.label = name;
      swatch.dataset.radioId = radio.id;
      swatch.setAttribute('aria-label', name + (isUnavailable ? ' (unavailable)' : ''));
      swatch.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      if (kind === 'duo') {
        const pair = DUO_MAP[normalise(name)];
        if (pair) {
          swatch.style.background = `linear-gradient(135deg, ${pair[0]} 50%, ${pair[1]} 50%)`;
          if (isLightColor(pair[0]) || isLightColor(pair[1])) {
            swatch.style.borderColor = 'rgba(0,0,0,0.15)';
          }
        } else {
          swatch.style.backgroundColor = '#ccc';
        }
      } else {
        const hex = COLOR_MAP[normalise(name)];
        if (hex) {
          swatch.style.backgroundColor = hex;
          if (isLightColor(hex)) swatch.style.borderColor = 'rgba(0,0,0,0.15)';
        } else {
          swatch.style.backgroundColor = '#ccc';
        }
      }

      if (isSelected) {
        swatch.classList.add('is-selected');
        selectedLabel.textContent = name;
      }
      if (isUnavailable) {
        swatch.classList.add('is-unavailable');
        swatch.setAttribute('aria-disabled', 'true');
      }

      swatch.addEventListener('click', () => {
        if (isUnavailable) return;
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
      });

      container.appendChild(swatch);
    });

    legend.insertAdjacentElement('afterend', container);

    const checked = radios.find((r) => r.checked);
    if (checked) selectedLabel.textContent = checked.value;

    buildPreview(fieldset, container, kind, imageMap);
  }

  // ── Preview thumbnail(s) below swatches ────────────────────
  function buildPreview(fieldset, afterEl, kind, imageMap) {
    let preview = fieldset.querySelector('.bundle-yarn-preview');
    if (!preview) {
      preview = document.createElement('div');
      preview.className = 'bundle-yarn-preview';
      if (kind === 'duo') {
        preview.innerHTML =
          '<div class="bundle-yarn-preview__images">' +
            '<img alt="" />' +
            '<img alt="" />' +
          '</div>' +
          '<span class="bundle-yarn-preview-label">Your colors</span>';
      } else {
        preview.innerHTML =
          '<div class="bundle-yarn-preview__images"><img alt="" /></div>' +
          '<span class="bundle-yarn-preview-label">Your yarn</span>';
      }
      afterEl.insertAdjacentElement('afterend', preview);
    } else {
      afterEl.insertAdjacentElement('afterend', preview);
    }
    updatePreview(fieldset, kind, imageMap);
  }

  function updatePreview(fieldset, kind, imageMap) {
    const preview = fieldset.querySelector('.bundle-yarn-preview');
    if (!preview) return;
    const radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
    const checked = radios.find((r) => r.checked);
    if (!checked) return;

    const imgs = preview.querySelectorAll('img');

    if (kind === 'duo') {
      const entryA = imageMap[toImageKey(DUOA_IMAGE_PREFIX, checked.value)];
      const entryB = imageMap[toImageKey(DUOB_IMAGE_PREFIX, checked.value)];
      if (entryA && entryA.url) {
        imgs[0].src = entryA.url;
        imgs[0].alt = checked.value + ' (color 1)';
      }
      if (entryB && entryB.url) {
        imgs[1].src = entryB.url;
        imgs[1].alt = checked.value + ' (color 2)';
      }
      preview.style.display = (entryA || entryB) ? '' : 'none';
    } else {
      const entry = imageMap[toImageKey(COLOR_IMAGE_PREFIX, checked.value)];
      if (entry && entry.url) {
        imgs[0].src = entry.url;
        imgs[0].alt = checked.value;
        preview.style.display = '';
      } else {
        preview.style.display = 'none';
      }
    }
  }

  // ── Main enhance pass ───────────────────────────────────────
  function enhance() {
    const variantSelects = document.querySelector('variant-selects');
    if (!variantSelects) return;

    const found = findSwatchFieldset(variantSelects);
    if (!found) return;

    const imageMap = buildImageMap();

    addBundleLabel(variantSelects);
    buildSwatches(found.fieldset, found.kind, imageMap);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', enhance);
  } else {
    enhance();
  }

  let pending = false;
  const productInfo = document.querySelector('product-info') || document.body;
  const observer = new MutationObserver(() => {
    if (pending) return;
    pending = true;
    requestAnimationFrame(() => {
      enhance();
      pending = false;
    });
  });
  observer.observe(productInfo, { childList: true, subtree: true });

  document.addEventListener('change', (e) => {
    if (e.target.tagName !== 'INPUT' || e.target.type !== 'radio') return;
    const fieldset = e.target.closest('.product-form__input--pill, .product-form__input--swatch');
    if (!fieldset) return;
    const legend = fieldset.querySelector('legend');
    if (!legend) return;
    const legendText = normalise(legend.textContent);
    const imageMap = buildImageMap();

    if (legendText.trim().startsWith('kit')) {
      const imageKey = KIT_IMAGE_KEYS[normalise(e.target.value)];
      const entry = imageKey && imageMap[imageKey];
      if (entry && entry.mediaId) activateGalleryImage(entry.mediaId);
    }

    if (legendText.includes('yarn color')) {
      updatePreview(fieldset, 'color', imageMap);
    }
    if (legendText.includes('color duo')) {
      updatePreview(fieldset, 'duo', imageMap);
    }
  });
})();